import dayjs from "dayjs";


export function toFixed(value: any, decimals = 2, unity = "", value_if_null = "-"): string {
    if (value === "-") {
        return value_if_null;
    }
    return value || value === 0 ? parseFloat(value).toFixed(decimals) + unity : value_if_null;
}
export function listToFixed(list: any[], decimals = 2, unity = "", value_if_null = "-"): string {
    if (!list || list.length === 0) {
        return value_if_null;
    }
    return list.map((value) => toFixed(value, decimals, unity, value_if_null)).join(" - ");
}

// * EFQ-3850 — module-level defaults so callers that pass no `template`
// * (e.g. raw `formatDate(value)` calls outside the tenant config pipeline)
// * automatically inherit the tenant's configured Day.js format.
// * `ConfigProvider` calls `setDefaultDateFormats(...)` whenever the
// * resolved tenant formats change.
const HARDCODED_DEFAULT_DATE_FORMAT = "DD-MM-YYYY";
const HARDCODED_DEFAULT_DATETIME_FORMAT = "HH:mm DD-MM-YYYY";

const _defaultDateFormats = {
    date_format: HARDCODED_DEFAULT_DATE_FORMAT,
    datetime_format: HARDCODED_DEFAULT_DATETIME_FORMAT,
};

export function setDefaultDateFormats(
    formats: { date_format?: string | null; datetime_format?: string | null } = {}
) {
    _defaultDateFormats.date_format = formats.date_format || HARDCODED_DEFAULT_DATE_FORMAT;
    _defaultDateFormats.datetime_format = formats.datetime_format || HARDCODED_DEFAULT_DATETIME_FORMAT;
}

export function getDefaultDateFormats() {
    return { ..._defaultDateFormats };
}

// * EFQ-3884 — translate a Day.js date format (used in tenant root config,
// * e.g. `DD-MM-YYYY`, `MM/DD/YYYY`) into the date-fns tokens expected by
// * `react-datepicker`'s `dateFormat` prop (e.g. `dd-MM-yyyy`, `MM/dd/yyyy`).
// * Only day/month/year tokens differ between the two libraries; separators
// * and any other characters are preserved as-is.
export function dayjsToDatePickerFormat(template?: string | null): string {
    const fmt = template || _defaultDateFormats.date_format;
    return fmt
        .replace(/YYYY/g, "yyyy")
        .replace(/YY/g, "yy")
        .replace(/DD/g, "dd")
        .replace(/D/g, "d");
    // * MM / M (month) tokens are identical in Day.js and date-fns.
}

// * EFQ-3884 — derive a human placeholder (all-lowercase) from a Day.js
// * date format, e.g. `MM/DD/YYYY` -> `mm/dd/yyyy`.
export function dayjsToDatePickerPlaceholder(template?: string | null): string {
    const fmt = template || _defaultDateFormats.date_format;
    return fmt.toLowerCase();
}

// * Formats a date for use in filenames/sheet names. Replaces characters
// * that are invalid in Excel sheet names or OS filenames (/ \ : * ? [ ]).
export function formatDateForFilename(template?: string) {
    const fmt = template || _defaultDateFormats.datetime_format;
    return dayjs().format(fmt).replace(/[/\\:*?[\]]/g, "-");
}

// * Reset defaults to the hardcoded values. Used by tests.
export function resetDefaultDateFormats() {
    _defaultDateFormats.date_format = HARDCODED_DEFAULT_DATE_FORMAT;
    _defaultDateFormats.datetime_format = HARDCODED_DEFAULT_DATETIME_FORMAT;
}

export function formatDate(ts: any, template?: string, value_if_null = "-") {
    return formatDateTime(ts, template || _defaultDateFormats.date_format, value_if_null);
}

export function formatDateTime(ts: any, template?: string, value_if_null = "-") {
    template = template || _defaultDateFormats.datetime_format;
    if (!ts || ts === "None") { return value_if_null; }
    if (ts === value_if_null) { return value_if_null; }

    // Ugly code alert !! - Discuss with team, we are going to refactor this
    // * check if date is in ddmmyyyy format, if so convert it to yyyy-mm-dd
    const ddmmyyyyRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/;
    let formattedDate = ts;
    if (ddmmyyyyRegex.test(ts)) {
        const [day, month, year] = ts.split("-");
        formattedDate = `${year}-${month}-${day}`;
    }

    const output = dayjs(formattedDate).utcOffset(0, true);

    if (!output.isValid()) {
        return ts;
    }

    return output.format(template);
}

export function convert_flag_state_color(state: any): string {
    if (state) {
        const output = state.split("flag-").pop();
        return output;
    }
    return "light";
}


export function toPercentage(numerator: any, denominator: number | null | undefined, decimals = 0, value_if_null = "-"): string {
    if (numerator === value_if_null) {
        return value_if_null;
    }
    if (denominator && denominator > 0) {
        numerator = numerator || 0;
        return toFixed(round((numerator / denominator) * 100, decimals), decimals, "%");
    }

    return value_if_null;
}

export function toFraction(count: number, total: number | null | undefined, value_if_null = "-"): string {

    let status = value_if_null;

    if (count > 0) {
        total = total || 1;
        status = `${count}/${total}`;
    }

    return status;
}

export function round(value: number, decimals: number) {
    const multiplier = 10 ** (decimals || 0);
    return Math.round(value * multiplier) / multiplier;
}


//  remove this function just use weight field in fruit table and use internal data processing to set the flag
export function checkUnderweight(weight: string, box_size: string, min_weight: Array<{ box_size: number; weight: number; }>): string {

    if (!min_weight) {
        return "-";
    }

    const int_weight = parseInt(weight, 10);
    const int_box_size = parseInt(box_size, 10);
    const minWeightForBox = min_weight.find((item) => item.box_size === int_box_size)?.weight;

    if (minWeightForBox === undefined || !int_weight || !int_box_size) {
        return "-";
    }

    if (int_weight < minWeightForBox) {
        return "Yes";
    } return "No";
}

// * metric are stored as values between 0 and 1, this function converts it to a percentage
export const metricPercentage = (value, decimals, value_if_null) => toFixed(value * 100, decimals, "%", value_if_null);

export function listToString(list: string[], separator = ", ", value_if_null = "-"): string {
    if (!list || list.length === 0) {
        return value_if_null;
    }
    return list.join(separator);
}

export const FORMATTERS = {
    toFixed,
    formatDate,
    formatDateTime,
    metricPercentage,
    listToString,
    listToFixed
    // toFraction,
};

// * this function setups a callback that can be used as value filter for FIELDS api
// * we could in theory also setup other perecentages here. but we should enforce business consulatant to use internal data processing to actually store the perccentage in the DB,
// * as that will also make it posibble to filter / order on those fields
export const setupFormatter = (formatter_key, { decimals, unity, value_if_null, datetime_format, seperator }) => {
    switch (formatter_key) {
    case "toFixed":
        return (value) => toFixed(value, decimals, unity, value_if_null);
    case "formatDate":
        return (value) => formatDate(value, datetime_format, value_if_null);
    case "formatDateTime":
        return (value) => formatDateTime(value, datetime_format, value_if_null);
    case "metricPercentage":
        return (value) => metricPercentage(value, decimals, value_if_null);
    case "listToString":
        return (value) => listToString(value, seperator || ", ", value_if_null);
    // cannot do this with a formatter, as it requires to read another field from unkown object
    // case "toFraction":
    //     return (value, object) => toFraction(value, object?.[denominator], value_if_null);
    default:
        return (value) => value;
    }

};
